import styles from './SlotPicker.module.scss';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailabilityCalendar } from '@spotted-zebra-uk/ui-components';
import {
  SlotTimes,
  dateToDayKey,
  formatDayFull,
  formatSlotRange,
  groupSlotsByDay,
  zonedDayKey,
} from '../../timezone';
import SlotList from '../SlotList/SlotList';

export interface PickableSlot extends SlotTimes {
  seatId: string;
}

interface SlotPickerProps<T extends PickableSlot> {
  slots: T[];
  timeZone: string;
  /** The slot picked so far, if any. Its day is the one shown. */
  selectedSeatId: string | null;
  /** Fired with `null` when the candidate moves to another day. */
  onSelect: (seatId: string | null) => void;
}

/**
 * A calendar of the days with times on offer and the times of the chosen
 * day, shared by the booking page and the reschedule page. The day shown
 * follows the picked slot, so changing the time zone never leaves a picked
 * time on a day that is no longer on screen.
 */
const SlotPicker = <T extends PickableSlot>({
  slots,
  timeZone,
  selectedSeatId,
  onSelect,
}: SlotPickerProps<T>) => {
  const { t } = useTranslation();

  const days = useMemo(
    () => groupSlotsByDay(slots, timeZone),
    [slots, timeZone]
  );
  const selectedSlot =
    slots.find(slot => slot.seatId === selectedSeatId) ?? null;

  // The day the candidate browsed to without picking a time on it.
  const [browsedDayKey, setBrowsedDayKey] = useState<string | null>(null);
  const activeKey = selectedSlot
    ? zonedDayKey(selectedSlot.startsAt, timeZone)
    : browsedDayKey;

  // A day browsed in one zone may not exist in another; fall back to the first.
  const dayIndex = days.findIndex(d => d.key === activeKey);
  const activeIndex = dayIndex >= 0 ? dayIndex : days.length > 0 ? 0 : -1;
  const activeDay = activeIndex >= 0 ? days[activeIndex] : null;

  const chooseDay = (key: string) => {
    setBrowsedDayKey(key);
    onSelect(null);
  };

  return (
    <div className={styles.picker}>
      <AvailabilityCalendar
        availableDates={days.map(d => d.date)}
        selectedDate={activeDay?.date ?? null}
        initialMonth={activeDay?.date ?? days[0]?.date}
        onSelect={date => chooseDay(dateToDayKey(date))}
        ariaLabel={t('publicScheduling.availableDates')}
        previousMonthAriaLabel={t('publicScheduling.previousMonth')}
        nextMonthAriaLabel={t('publicScheduling.nextMonth')}
        chooseDayAriaLabelPrefix={t('publicScheduling.chooseDay')}
        unavailableDayAriaLabelPrefix={t('publicScheduling.unavailableDay')}
      />
      <SlotList
        dayLabel={
          activeDay
            ? formatDayFull(activeDay.slots[0].startsAt, timeZone)
            : t('publicScheduling.noDaySelected')
        }
        slots={(activeDay?.slots ?? []).map(slot => ({
          id: slot.seatId,
          label: formatSlotRange(slot, timeZone),
        }))}
        selectedSlotId={selectedSeatId}
        onSelect={onSelect}
        hasPreviousDay={activeIndex > 0}
        hasNextDay={activeIndex >= 0 && activeIndex < days.length - 1}
        onPreviousDay={() => chooseDay(days[activeIndex - 1].key)}
        onNextDay={() => chooseDay(days[activeIndex + 1].key)}
      />
    </div>
  );
};

export default SlotPicker;
